import React from 'react';

// --- Main List Data from Screenshot ---
const mainListItems = [
  "Documenting Stages of Vulnerabilities Using Tools",
  "VAPT Reports: Developer Report v/s Higher Management Report",
  "Concepts of Code Security and Patching",
  "Parts of a VAPT Report",
  "Common Good Practices and Bad Practices",
];

// --- Outcomes List Data from Screenshot ---
const outcomesListItems = [
  "HTTP, HTTPS, APIs (protocols/frameworks)",
  "cookies & sessions (web technologies)",
  "SQLi (SQL Injection)",
  "XSS (Cross-Site Scripting)",
  "SSRF (Server-Side Request Forgery)",
  "RCE (Remote Code Execution)",
  "WAF (Web Application Firewall) (a type of defense system)",
];

// --- Internal CSS for all styling ---
const internalCSS = `
  /* --- Main Page Container --- */
  .chapter-page-container {
    background-color: #fff; /* White background for the whole page */
    font-family: 'Poppins', 'Open Sans', sans-serif; /* Consistent font */
    position: relative; /* Needed for the wavy bottom */
  }

  /* --- 1. Top Banner Section --- */
  .chapter-top-banner {
    position: relative;
    width: 100%;
    height: 350px; 
    overflow: hidden;
    display: flex;
    flex-direction: column; 
    justify-content: center; 
    background-color: #1a1a1a; 
    padding: 1rem 2rem; 
  }

  .chapter-banner-bg-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    opacity: 0.5; /* Dim the image */
  }

  .chapter-banner-content {
    position: relative;
    z-index: 1;
    color: #fff;
    text-align: left;
    max-width: 960px; /* Max width for content */
    margin: 0 auto; /* Center content horizontally */
    width: 100%;
  }

  .chapter-badge {
    background-color: #dc3545; /* Red color */
    color: #fff;
    font-size: 0.9rem;
    font-weight: 600;
    padding: 0.2rem 0.75rem;
    border-radius: 4px;
    display: inline-block; 
    margin-bottom: 0.75rem;
  }

  /* --- 2. List Section --- */
  .chapter-list-section {
    padding: 4rem 1rem;
    background-color: #fff;
    position: relative; /* To appear above the wavy bottom */
    z-index: 1;
  }

  .chapter-list-container {
    max-width: 960px; /* Align with banner content */
    margin: 0 auto;
    width: 100%;
  }

  .chapter-list {
    list-style-type: disc; /* Default bullet style */
    padding-left: 1.5rem; /* Standard indent for list */
  }

  .chapter-list li {
    font-size: 1.15rem;
    line-height: 1.8;
    color: #333;
    margin-bottom: 0.5rem; /* Space between list items */
    font-weight: 500; /* Medium weight, as seen in image */
  }

  /* --- Tools and Outcomes Section --- */
  .outcomes-title {
    font-size: 1.75rem;
    font-weight: 700; /* Bold */
    color: #212529; /* Dark color */
    margin-top: 3rem;  /* Space above */
    margin-bottom: 1.5rem;
    text-transform: uppercase; /* All caps */
    /* No underline per Chapter 9 image */
  }
  
  /* Styling for the outcomes list */
  .outcomes-list li {
    font-size: 1.1rem;
    font-weight: 500; /* Medium weight */
    line-height: 1.7;
  }
  
  /* --- 3. Wavy Bottom Section --- */
  .wavy-bottom-svg {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 0;
    pointer-events: none; /* Make sure it's not clickable */
  }

  /* --- Responsive Adjustments --- */
  @media (max-width: 991.98px) { /* Tablets */
    .chapter-top-banner {
      height: 300px;
    }
    .chapter-list li {
      font-size: 1.05rem;
      line-height: 1.7;
    }
  }

  @media (max-width: 767.98px) { /* Mobile Devices */
    .chapter-top-banner {
      height: 130px;
      justify-content: flex-end; 
      padding-bottom: 1.5rem;
      padding-left: 1rem;
      padding-right: 1rem;
    }
    .chapter-banner-content {
     text-align: start !important;
    }
    .chapter-badge {
      font-size: 0.8rem;
      padding: 0.15rem 0.5rem;
      margin-bottom: 0.5rem;
    }
    .chapter-list-section {
      padding: 2rem 1rem;
      padding-bottom: 4rem; /* Add padding to see wave */
    }
    .chapter-list {
      padding-left: 1.25rem; /* Reduce indent on mobile */
    }
    .chapter-list li {
      font-size: 0.95rem; /* Smaller text on mobile */
      line-height: 1.6;
      margin-bottom: 0.4rem;
    }
    
    /* Responsive for Tools/Outcomes */
    .outcomes-title {
      font-size: 1.4rem;
      margin-top: 2rem;
      margin-bottom: 1rem;
    }
    .outcomes-list li {
      font-size: 0.95rem;
    }
  }
`;

const Chapter9Page = () => {
  return (
    <>
      <style>{internalCSS}</style>
      
      <div className="chapter-page-container">
        
        {/* --- 1. Top Banner Section --- */}
        <div className="chapter-top-banner">
          <img 
            src="https://skillsuprise.com/data/cyberPDF/chapters/chapter9.png" 
            alt="Chapter 9 Background" 
            className="chapter-banner-bg-img" 
          />
          <div className="chapter-banner-content">
            {/* This banner only has the badge, no title */ }
            <div>
              <span className="chapter-badge">Chapter 9</span>
            </div>
          </div>
        </div>
        
        {/* --- 2. List Section --- */}
        <div className="chapter-list-section">
          <div className="chapter-list-container">
            
            {/* --- Main Bullet List --- */}
            <ul className="chapter-list">
              {mainListItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            
            {/* --- Tools & Outcomes Section --- */}
            <h2 className="outcomes-title">TOOLS AND CHAPTER OUTCOMES</h2>
            
            <ul className="chapter-list outcomes-list">
              {outcomesListItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            
          </div>
        </div>
        
        {/* --- 3. Wavy Bottom SVG --- */}
        <svg 
          className="wavy-bottom-svg"
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1440 320"
          style={{ height: '100px' }} // Control the wave height
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#8E9DFF', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#C0C8FF', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <path 
            fill="url(#waveGradient)" 
            fillOpacity="0.7" 
            d="M0,224L48,213.3C96,203,192,181,288,176C384,171,480,181,576,202.7C672,224,768,256,864,261.3C960,267,1056,245,1152,218.7C1248,192,1344,160,1392,144L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>

      </div>
    </>
  );
};

export default Chapter9Page;